import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, Mail, MapPin, Package, Receipt, Sparkles, Truck } from "lucide-react";
import { MolecularBg } from "../components/MolecularBg";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { ProductImage } from "../components/ProductImage";
import { trackPurchase } from "../lib/tracker";

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 12; // ~30s — Stripe usually flips to 'paid' within 2-5s

export const CheckoutSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const { clear } = useCart();
    const { user } = useAuth();
    const [phase, setPhase] = useState("verifying"); // verifying | paid | failed | expired | timeout | error
    const [order, setOrder] = useState(null);
    const [errMsg, setErrMsg] = useState(null);
    const attemptsRef = useRef(0);
    const cleared = useRef(false);
    const stopped = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setPhase("error");
            setErrMsg("Missing checkout session reference in URL.");
            return;
        }
        if (!sessionId.startsWith("cs_")) {
            setPhase("error");
            setErrMsg("Invalid checkout session reference.");
            return;
        }

        // Reset per-mount refs so React StrictMode (or any remount) does not leave
        // `stopped` permanently true after the first cleanup.
        stopped.current = false;
        attemptsRef.current = 0;

        const poll = async () => {
            if (stopped.current) return;
            try {
                const { data } = await api.get(`/checkout/status/${sessionId}`);
                setOrder(data);

                if (data.payment_status === "paid") {
                    setPhase("paid");
                    stopped.current = true;
                    if (!cleared.current) {
                        clear();
                        cleared.current = true;
                        try { trackPurchase(Number(data.amount || 0), data.reference); } catch {}
                    }
                    return;
                }
                if (data.payment_status === "failed" || data.status === "failed") {
                    setPhase("failed");
                    stopped.current = true;
                    return;
                }
                if (data.payment_status === "expired" || data.status === "expired") {
                    setPhase("expired");
                    stopped.current = true;
                    return;
                }

                attemptsRef.current += 1;
                if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
                    setPhase("timeout");
                    stopped.current = true;
                    return;
                }
                setTimeout(poll, POLL_INTERVAL_MS);
            } catch (e) {
                const detail = e?.response?.data?.detail;
                // Treat 404 (no txn yet) as a transient state — keep polling.
                if (e?.response?.status === 404 && attemptsRef.current < MAX_POLL_ATTEMPTS) {
                    attemptsRef.current += 1;
                    setTimeout(poll, POLL_INTERVAL_MS);
                    return;
                }
                setErrMsg(detail || "Unable to reach payment verification service.");
                setPhase("error");
                stopped.current = true;
            }
        };

        poll();
        return () => { stopped.current = true; };
         
    }, [sessionId]);

    return (
        <div className="pt-32 pb-20 min-h-screen relative" data-testid="checkout-success-page">
            <MolecularBg intensity="medium" />
            <div className="container-x relative z-10 max-w-3xl">
                {phase === "verifying" && <VerifyingState />}
                {phase === "paid" && <PaidState order={order} isLoggedIn={!!user} />}
                {phase === "failed" && <FailedState errMsg={errMsg} />}
                {phase === "expired" && <ExpiredState />}
                {phase === "timeout" && <TimeoutState reference={order?.reference} />}
                {phase === "error" && <ErrorState errMsg={errMsg} />}
            </div>
        </div>
    );
};

const VerifyingState = () => (
    <div className="glass-elevated rounded-3xl p-10 md:p-14 text-center neon-border-cyan" data-testid="verifying-state">
        <Loader2 size={56} className="mx-auto mb-5 text-[var(--neon-cyan)] animate-spin" />
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white mb-3">
            Verifying your payment...
        </h1>
        <p className="text-[var(--text-secondary)] max-w-md mx-auto">
            We&apos;re confirming your order with Stripe. This usually takes just a few seconds.
        </p>
    </div>
);

const PaidState = ({ order, isLoggedIn }) => {
    const amount = order?.amount ?? (order?.amount_total ? order.amount_total / 100 : null);
    const currency = (order?.currency || "aud").toUpperCase();
    const reference = order?.reference;
    const email = order?.customer_email;
    const items = order?.items || [];
    const shipping = order?.shipping_address;
    const shippingMethod = order?.shipping_method;
    const shippingCost = Number(order?.shipping_cost ?? shippingMethod?.price ?? 0);

    return (
        <div className="glass-elevated rounded-3xl p-8 md:p-12 neon-border-cyan" data-testid="paid-state">
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/10 border-2 border-emerald-400/40 mb-4">
                    <CheckCircle2 size={42} className="text-emerald-400" data-testid="paid-check" />
                </div>
                <span className="eyebrow text-emerald-400">// Payment Confirmed</span>
                <h1 className="font-display text-4xl md:text-5xl font-extrabold text-white tracking-tighter mt-2 mb-3" data-testid="success-heading">
                    Thank you for your order
                </h1>
                <p className="text-[var(--text-secondary)] max-w-xl mx-auto">
                    Your research peptides are now in our Sydney dispatch queue. A confirmation email with your order summary and Certificate of Analysis has been sent.
                </p>
            </div>

            {/* Order summary card */}
            <div className="glass rounded-2xl p-5 md:p-6 mb-6" data-testid="order-summary-card">
                <div className="flex items-start justify-between gap-4 mb-5 pb-4 border-b border-white/5">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] flex items-center gap-1.5">
                            <Receipt size={11} /> Order Reference
                        </div>
                        <div className="font-mono text-xl md:text-2xl font-bold text-[var(--gold)] mt-1" data-testid="order-reference">
                            {reference || "—"}
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Total Paid</div>
                        <div className="font-display text-2xl md:text-3xl font-extrabold text-white mt-1" data-testid="order-total">
                            {amount !== null ? `A$${Number(amount).toFixed(2)}` : "—"}
                            <span className="text-sm font-mono text-[var(--text-muted)] ml-2">{currency}</span>
                        </div>
                    </div>
                </div>

                {email && (
                    <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mb-4">
                        <Mail size={14} className="text-[var(--gold)]" />
                        <span>Confirmation sent to <span className="text-white font-semibold" data-testid="customer-email">{email}</span></span>
                    </div>
                )}

                {items.length > 0 && (
                    <div className="mb-4">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
                            <Package size={11} /> Items
                        </div>
                        <ul className="space-y-2" data-testid="order-items-list">
                            {items.map((it, idx) => (
                                <li key={idx} className="flex justify-between items-baseline text-sm" data-testid={`order-item-${idx}`}>
                                    <span className="text-white">
                                        {it.name || it.product_slug}
                                        {it.dosage && <span className="text-[var(--text-muted)] text-xs ml-2 font-mono">{it.dosage}</span>}
                                        <span className="text-[var(--text-muted)] ml-2">× {it.quantity}</span>
                                    </span>
                                    {it.line_total != null && (
                                        <span className="text-[var(--text-secondary)] font-mono">A${Number(it.line_total).toFixed(2)}</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {shipping && shipping.full_name && (
                    <div className="pt-4 border-t border-white/5">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-2 flex items-center gap-1.5">
                            <MapPin size={11} /> Shipping To
                        </div>
                        <div className="text-sm text-white" data-testid="shipping-address">
                            <div className="font-semibold">{shipping.full_name}</div>
                            <div className="text-[var(--text-secondary)]">{shipping.line1}{shipping.line2 ? `, ${shipping.line2}` : ""}</div>
                            <div className="text-[var(--text-secondary)]">{shipping.suburb}, {shipping.state} {shipping.postcode}</div>
                            <div className="text-[var(--text-secondary)]">{shipping.country}</div>
                        </div>
                    </div>
                )}

                {shippingMethod && shippingMethod.name && (
                    <div className="pt-4 mt-4 border-t border-white/5 flex items-start gap-2" data-testid="shipping-method-summary">
                        <Truck size={14} className="text-[var(--gold)] mt-0.5 shrink-0" />
                        <div className="flex-1">
                            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                                Shipping method
                            </div>
                            <div className="text-sm text-white font-semibold" data-testid="shipping-method-name">
                                {shippingMethod.name}
                                <span className="text-[var(--text-muted)] font-mono ml-2 text-xs">
                                    {shippingCost > 0 ? `A$${shippingCost.toFixed(2)}` : "Free"}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="glass rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="dispatch-notice">
                <Package size={18} className="text-[var(--gold)] mt-0.5 shrink-0" />
                <div className="text-sm text-[var(--text-secondary)]">
                    <span className="text-white font-semibold">Cold-chain dispatch within 1 business day</span> from our Sydney facility.
                    Tracking details will be emailed as soon as your shipment leaves the lab.
                </div>
            </div>

            {reference && <UpsellRow reference={reference} purchasedNames={items.map(i => i.name || i.product_slug)} />}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/shop" className="btn-neon" data-testid="continue-shopping">
                    Continue shopping <ArrowRight size={14} />
                </Link>
                {reference && (
                    <Link to={`/order/${reference}`} className="btn-solid" data-testid="view-order">
                        View order details <ArrowRight size={14} />
                    </Link>
                )}
                {isLoggedIn && (
                    <Link to="/account" className="btn-ghost" data-testid="view-account">
                        My account
                    </Link>
                )}
            </div>

            <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] text-center mt-8">
                For research use only · Not for human consumption
            </p>
        </div>
    );
};

const FailedState = ({ errMsg }) => (
    <div className="glass-elevated rounded-3xl p-10 md:p-12 text-center" data-testid="failed-state">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-500/10 border-2 border-red-400/40 mb-4">
            <AlertCircle size={42} className="text-red-400" />
        </div>
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-white mb-3">
            Payment failed
        </h1>
        <p className="text-[var(--text-secondary)] mb-6 max-w-md mx-auto">
            {errMsg || "Your card was declined or the payment couldn't be completed. Your cart is still saved — try a different card or payment method."}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/cart" className="btn-neon" data-testid="retry-payment">Retry payment</Link>
            <Link to="/shop" className="btn-ghost">Back to shop</Link>
        </div>
    </div>
);

const ExpiredState = () => (
    <div className="glass-elevated rounded-3xl p-10 md:p-12 text-center" data-testid="expired-state">
        <AlertCircle size={48} className="mx-auto mb-4 text-amber-400" />
        <h1 className="font-display text-3xl font-extrabold text-white mb-3">Checkout session expired</h1>
        <p className="text-[var(--text-secondary)] mb-6 max-w-md mx-auto">
            Stripe checkout sessions expire after 24 hours. Your cart is still saved — return to cart to restart payment.
        </p>
        <Link to="/cart" className="btn-neon">Back to cart</Link>
    </div>
);

const TimeoutState = ({ reference }) => (
    <div className="glass-elevated rounded-3xl p-10 md:p-12 text-center" data-testid="timeout-state">
        <Loader2 size={48} className="mx-auto mb-4 text-amber-400" />
        <h1 className="font-display text-3xl font-extrabold text-white mb-3">Still confirming...</h1>
        <p className="text-[var(--text-secondary)] mb-6 max-w-md mx-auto">
            Your payment is taking longer than usual to confirm. This sometimes happens with bank verification.
            We&apos;ll send you an email as soon as it clears — no need to pay again.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => window.location.reload()} className="btn-neon" data-testid="check-again">Check again</button>
            {reference && <Link to={`/order/${reference}`} className="btn-solid">View order</Link>}
            <Link to="/shop" className="btn-ghost">Back to shop</Link>
        </div>
    </div>
);

const ErrorState = ({ errMsg }) => (
    <div className="glass-elevated rounded-3xl p-10 md:p-12 text-center" data-testid="error-state">
        <AlertCircle size={48} className="mx-auto mb-4 text-red-400" />
        <h1 className="font-display text-3xl font-extrabold text-white mb-3">We couldn&apos;t verify your payment</h1>
        <p className="text-[var(--text-secondary)] mb-6 max-w-md mx-auto" data-testid="error-message">
            {errMsg || "Something went wrong while confirming your order."} If your card was charged, please reply to your Stripe receipt or email <a href="mailto:carbonlabsau@outlook.com" className="text-[var(--gold)] underline">carbonlabsau@outlook.com</a> with the session ID from this URL — we&apos;ll resolve it within one business day.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => window.location.reload()} className="btn-neon" data-testid="error-retry">Try again</button>
            <Link to="/cart" className="btn-ghost">Back to cart</Link>
        </div>
    </div>
);

const UpsellRow = ({ reference, purchasedNames = [] }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/orders/${reference}/upsells`, { params: { limit: 3 } });
                if (!cancelled) setItems(Array.isArray(data) ? data : []);
            } catch {
                if (!cancelled) setItems([]);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [reference]);

    if (loading || items.length === 0) return null;

    const firstName = purchasedNames[0]?.split(" ")[0] || "this stack";

    return (
        <div className="mb-6" data-testid="upsell-row">
            <div className="flex items-center gap-2 mb-3">
                <Sparkles size={14} className="text-[var(--gold)]" />
                <span className="eyebrow text-[var(--gold)]">
                    // Researchers also stacked {firstName} with
                </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {items.slice(0, 3).map((p) => (
                    <UpsellCard key={p.slug} product={p} />
                ))}
            </div>
            <p className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-3">
                Curated by Carbon Lab researchers · Combined-target protocols
            </p>
        </div>
    );
};

const UpsellCard = ({ product }) => {
    const price = product.sale_price ?? product.price;
    const onSale = product.sale_price != null && product.sale_price < product.price;
    return (
        <Link
            to={`/product/${product.slug}`}
            className="glass rounded-xl p-3 border border-white/5 hover:border-[var(--gold)]/40 transition-colors group block"
            data-testid={`upsell-card-${product.slug}`}
        >
            <ProductImage product={product} ratio="3 / 2" className="rounded-lg mb-2">
                {onSale && (
                    <span className="absolute top-1.5 left-1.5 text-[9px] font-mono uppercase tracking-wider bg-[var(--gold)] text-black px-1.5 py-0.5 rounded z-10">
                        Sale
                    </span>
                )}
            </ProductImage>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                {product.dosage}
            </div>
            <div className="text-sm font-semibold text-white truncate group-hover:text-[var(--gold)] transition-colors">
                {product.name}
            </div>
            <div className="flex items-baseline gap-2 mt-1">
                <span className="text-base font-bold text-[var(--gold)]">A${Number(price).toFixed(2)}</span>
                {onSale && (
                    <span className="text-xs text-[var(--text-muted)] line-through">
                        A${Number(product.price).toFixed(2)}
                    </span>
                )}
            </div>
        </Link>
    );
};

